import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'

export default function CustomGazebos() {
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  const faqs = [
    {
      q: "Do I need a building permit for a gazebo in Calgary?",
      a: "Yes. Many permanent gazebos require permits through the City of Calgary, especially structures exceeding municipal size thresholds or requiring structural foundations. We handle the entire permit process for our clients."
    },
    {
      q: "How deep do gazebo footings need to be in Calgary?",
      a: "Footings must typically extend below Calgary’s frost line to prevent movement caused by seasonal freeze-thaw cycles. Final depth depends on soil conditions and engineering requirements."
    },
    {
      q: "Is Western Red Cedar better than pressure-treated lumber?",
      a: "Western Red Cedar offers natural resistance to moisture, insects, and UV damage, making it ideal for Alberta’s climate while providing a premium architectural appearance."
    },
    {
      q: "Can you build a gazebo on an existing deck?",
      a: "Yes. Existing decks can sometimes support a gazebo, but structural load calculations and framing inspections are required before installation."
    },
    {
      q: "How long does a custom gazebo project take?",
      a: "Most projects are completed within 1–3 weeks after design approval and permit issuance."
    }
  ]

  const toggleFaq = (index: number) => setOpenFaq(openFaq === index ? null : index)
  
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Custom Gazebo Construction",
        "provider": { 
          "@type": "LocalBusiness", 
          "name": "Busy Beaver Construction", 
          "address": { 
            "@type": "PostalAddress", 
            "addressLocality": "Calgary", 
            "addressRegion": "AB" 
          } 
        },
        "areaServed": "Calgary, AB",
        "description": "Structurally engineered custom gazebos built for Alberta's extreme climate and heavy snow loads."
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      }
    ]
  }

  return (
    <main className="bg-white">
      <Helmet>
        <title>Gazebo Builders Calgary | Custom Solid-Roof Outdoor Structures</title>
        <meta name="description" content="Calgary's premier custom gazebo builders. Engineered for 3.0 kPa snow loads, Chinook winds, and Alberta weather. 25+ years experience. Free onsite estimates." />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />

      {/* Hero Section */}
      <section className="relative h-[550px] flex items-center justify-center overflow-hidden">
        <img 
          src="/assets/images/Other/Living Spaces/Outdoor-covered-Living-Space-on-Mahogany-lake.jpeg" 
          alt="Custom structurally engineered gazebo in Calgary" 
          className="absolute inset-0 w-full h-full object-cover" 
        />
        <div className="absolute inset-0 bg-black/65"></div>
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-primary text-white mb-6 uppercase leading-tight">
            Gazebo Builders Calgary
          </h1>
          <p className="text-gray-200 text-lg md:text-xl font-text mb-8 leading-relaxed">
            At Busy Beaver Construction, we have designed and built custom gazebos for homeowners across Calgary and surrounding communities for over 28 Years. Unlike prefabricated kits, our permanent outdoor structures are engineered for Alberta’s extreme climate, including heavy snow loads, Chinook temperature swings, intense UV exposure, and repeated freeze-thaw cycles.
          </p>
          <div className="flex justify-center">
            <a href="/appointment" className="px-10 py-5 bg-beaver-green text-black font-bold uppercase rounded-lg shadow-2xl hover:bg-white transition-all font-accent" onClick={(e) => handleNavigation(e, '/appointment')}>
              Get Your Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Section 2: Professional Construction */}
      <section className="py-24 container mx-auto px-4 max-w-7xl">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          <div className="lg:w-3/5">
            <h2 className="text-3xl md:text-5xl font-primary mb-8 text-gray-900">Professional Gazebo Construction for Calgary Homeowners</h2>
            <p className="text-lg text-gray-700 leading-relaxed font-text mb-6">
              A custom gazebo is more than an outdoor feature—it is a permanent extension of your home, designed for year-round comfort, entertaining, and long-term property value. Every gazebo is custom designed using premium Western Red Cedar, structural-grade lumber, corrosion-resistant galvanized fasteners, and reinforced concrete footings built below Calgary’s frost line.
            </p>
            <p className="text-gray-700 font-text leading-relaxed mb-8">
              Busy Beaver builds custom gazebos ranging from intimate backyard retreats to fully integrated outdoor living spaces with privacy walls, composite flooring, lighting systems, and custom railings. We manage every stage of your project, from concept drawings and 3D modeling to permit approval with the City of Calgary.
            </p>
            <h4 className="font-bold text-gray-900 mb-4 uppercase">Our structures typically include:</h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-600 font-text mb-8">
              <li className="flex items-center gap-2"><i className="fas fa-check text-beaver-green"></i> Premium Western Red Cedar framing</li>
              <li className="flex items-center gap-2"><i className="fas fa-check text-beaver-green"></i> Pressure-treated structural supports</li>
              <li className="flex items-center gap-2"><i className="fas fa-check text-beaver-green"></i> Reinforced concrete foundations</li>
              <li className="flex items-center gap-2"><i className="fas fa-check text-beaver-green"></i> Systems engineered for 3.0 kPa snow loads</li>
              <li className="flex items-center gap-2"><i className="fas fa-check text-beaver-green"></i> Architectural shingles or metal roofing</li>
              <li className="flex items-center gap-2"><i className="fas fa-check text-beaver-green"></i> TimberTech and AZEK composite options</li>
              <li className="flex items-center gap-2"><i className="fas fa-check text-beaver-green"></i> Aluminum railing systems</li>
            </ul>
          </div>
          <div className="lg:w-2/5">
            <img src="/assets/images/Other/Living Spaces/Outdoor-covered-Living-Space-on-Mahogany-lake.jpeg" alt="Professional gazebo construction in Calgary" className="rounded-2xl shadow-xl border-4 border-white" />
          </div>
        </div>
      </section>

      {/* Why Built for Calgary Section */}
      <section className="py-24 bg-gray-900 text-white relative">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl md:text-5xl font-primary mb-16 text-center uppercase">Engineered for Calgary’s Climate, Soil, and Building Codes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="space-y-4 p-6 bg-gray-800 rounded-xl border border-gray-700">
              <h3 className="text-xl font-secondary text-beaver-green">Snow Load Engineering</h3>
              <p className="text-gray-400 text-sm leading-relaxed">Every gazebo is structurally designed to meet or exceed Alberta residential snow load requirements, including roof framing, beam sizing, load calculations, and engineered connections.</p>
            </div>
            <div className="space-y-4 p-6 bg-gray-800 rounded-xl border border-gray-700">
              <h3 className="text-xl font-secondary text-beaver-green">Frost-Protected Foundations</h3>
              <p className="text-gray-400 text-sm leading-relaxed">Our reinforced concrete footings are installed below Calgary’s frost depth to prevent seasonal movement caused by freeze-thaw cycles.</p>
            </div>
            <div className="space-y-4 p-6 bg-gray-800 rounded-xl border border-gray-700">
              <h3 className="text-xl font-secondary text-beaver-green">UV & Moisture Protection</h3>
              <p className="text-gray-400 text-sm leading-relaxed">We use naturally durable Western Red Cedar, premium stains, and corrosion-resistant hardware to withstand high UV exposure and temperature fluctuations.</p>
            </div>
            <div className="space-y-4 p-6 bg-gray-800 rounded-xl border border-gray-700">
              <h3 className="text-xl font-secondary text-beaver-green">Permit & Code Compliance</h3>
              <p className="text-gray-400 text-sm leading-relaxed">We prepare permit drawings, site plans, structural documentation, and coordinate submissions with the City of Calgary for smooth approvals.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 container mx-auto px-4 max-w-7xl">
        <h2 className="text-3xl md:text-5xl font-primary mb-16 text-center uppercase text-gray-900">The Busy Beaver Gazebo Building Process</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
          <div className="group">
            <div className="w-20 h-20 bg-gray-100 group-hover:bg-beaver-green rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold transition-all">1</div>
1.             <h4 className="font-bold mb-3 uppercase text-gray-900">On-Site Consultation
</h4>
            <p className="text-sm text-gray-600 font-text">We assess your backyard layout, property setbacks, drainage conditions, and design goals.</p>
          </div>
          <div className="group">
            <div className="w-20 h-20 bg-gray-100 group-hover:bg-beaver-green rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold transition-all">2</div>
            <h4 className="font-bold mb-3 uppercase text-gray-900">2. 3D Design & Engineering</h4>
            <p className="text-sm text-gray-600 font-text">Our drafting team creates detailed 3D renderings, structural layouts, and foundation plans.</p>
          </div>
          <div className="group">
            <div className="w-20 h-20 bg-gray-100 group-hover:bg-beaver-green rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold transition-all">3</div>
            <h4 className="font-bold mb-3 uppercase text-gray-900">3. Permit Preparation</h4>
            <p className="text-sm text-gray-600 font-text">We prepare site plans, construction drawings, and engineering documentation for municipal approval.</p>
          </div>
          <div className="group">
            <div className="w-20 h-20 bg-gray-100 group-hover:bg-beaver-green rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold transition-all">4</div>
            <h4 className="font-bold mb-3 uppercase text-gray-900">4. Professional Installation</h4>
            <p className="text-sm text-gray-600 font-text">Our experienced builders complete your project using professional-grade materials and precision craftsmanship.</p>
          </div>
        </div>
      </section>

      {/* Cost Section */}
      <section className="py-20 bg-gray-50 border-y border-gray-100 text-center">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-5xl font-primary text-gray-900 mb-8 uppercase">How Much Does a Custom Gazebo Cost in Calgary?</h2>
          <p className="text-lg text-gray-600 font-text mb-8">
            Custom gazebo pricing in Calgary typically ranges from <strong>$8,000 to $35,000+</strong>, depending on size, roof design, material selection, foundation requirements, and permit complexity. We provide detailed fixed-price estimates with no hidden surprises.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 container mx-auto px-4 max-w-4xl">
        <h2 className="text-3xl md:text-5xl font-primary text-center mb-16 uppercase text-gray-900">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white hover:border-beaver-green transition-all">
              <button 
                onClick={() => toggleFaq(i)} 
                className="w-full px-8 py-6 text-left flex justify-between items-center font-bold font-secondary"
              >
                <span className={openFaq === i ? 'text-beaver-green text-xl' : 'text-gray-800 text-lg'}>{faq.q}</span>
                <i className={`fas fa-chevron-down transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}></i>
              </button>
              {openFaq === i && (
                <div className="px-8 pb-8 text-gray-600 font-text text-lg border-t pt-6">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <AssociationsAndSuppliers />
      <Footer />
    </main>
  )
}